import React, { createContext, useContext, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Client } from "@/types";
import { useAppStore } from "@/core/store/useAppStore";
import { API_CONFIG } from "@/config/api";

interface ClientContextValue {
  selectedClient: Client | null;
  setClient: (client: Client) => Promise<void>;
}

const ClientContext = createContext<ClientContextValue | undefined>(undefined);

export { ClientContext };

interface ClientProviderProps {
  children: ReactNode;
}

export const ClientProvider: React.FC<ClientProviderProps> = ({ children }) => {
  const { selectedClient, setSelectedClient } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();

  const setClient = async (client: Client) => {
    setSelectedClient(client);

    await switchClientInBackground(client);

    // Navigate to same view but with the new client ID
    const currentPath = location.pathname;
    const viewMatch = currentPath.match(/\/app\/[^/]+\/(.+)/);
    const currentView = viewMatch ? viewMatch[1] : 'home';
    navigate(`/app/${client.id}/${currentView}`);
  };

  const switchClientInBackground = async (client: Client) => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        console.warn("No access token available for client switching");
        return;
      }

      const apiUrl = API_CONFIG.URL;
      const response = await fetch(`${apiUrl}/client/${client.id}/switch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.warn(`⚠️ Backend client switch failed: ${response.statusText}`);
      }
    } catch (error) {
      console.warn("⚠️ Error switching client in background:", error);
    }
  };

  const getAccessToken = (): string | null => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user.accessToken || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  return (
    <ClientContext.Provider value={{ selectedClient, setClient }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
};
